import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { useDebounce } from '@/hooks/useDebounce';
import { getCompanies } from '../api/crmApi';
import type { Company } from '../types';

interface CompanyComboboxProps {
  /** Currently selected company id, if any. */
  value?: string;
  /** Display name of the selected company (so the trigger reads right before the list loads). */
  valueName?: string;
  /** Fired with the picked company, or (undefined, undefined) when cleared. */
  onChange: (companyId: string | undefined, companyName: string | undefined) => void;
  disabled?: boolean;
  id?: string;
  'aria-labelledby'?: string;
}

/**
 * Searchable company picker. Mirrors the AccountSearchCombobox pattern already
 * used on the company-detail page (Popover + Command + debounced getCompanies).
 */
export function CompanyCombobox({
  value, valueName, onChange, disabled, id, 'aria-labelledby': ariaLabelledBy,
}: CompanyComboboxProps) {
  const { t } = useTranslation('crm');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    getCompanies({ search: debouncedSearch || undefined }, 0, 10)
      .then((data) => { if (!cancelled) setResults(data.items); })
      .catch(() => { if (!cancelled) setResults([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedSearch, open]);

  const triggerLabel = valueName || (value ? t('contacts.modal.companySelected') : t('contacts.modal.companyNone'));

  return (
    <div className="flex items-center gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-labelledby={ariaLabelledBy}
            disabled={disabled}
            className="w-full justify-between font-normal"
          >
            <span className={cn('truncate', !value && 'text-muted-foreground')}>{triggerLabel}</span>
            <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={t('contacts.modal.searchCompanies')}
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>
                {loading ? t('companies.links.searching') : t('contacts.modal.noCompaniesFound')}
              </CommandEmpty>
              <CommandGroup>
                {results.map((company) => (
                  <CommandItem
                    key={company.id}
                    value={company.id}
                    onSelect={() => {
                      onChange(company.id, company.name);
                      setOpen(false);
                    }}
                  >
                    <Check className={cn('mr-2 h-4 w-4', value === company.id ? 'opacity-100' : 'opacity-0')} />
                    <span className="truncate">{company.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value && !disabled && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0"
          aria-label={t('contacts.modal.clearCompany')}
          onClick={() => onChange(undefined, undefined)}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
